import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { 
  Image, 
  Trash2, 
  Edit, 
  Star, 
  StarOff,
  ArrowRightLeft, 
  Check,
  X,
  Plus,
  ChevronDown,
  ChevronUp,
  ImagePlus
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'terase', label: 'Terase' },
  { id: 'acoperisuri', label: 'Acoperișuri' },
  { id: 'fundatii', label: 'Fundații' },
  { id: 'speciale', label: 'Proiecte Speciale' },
];

const PortfolioManager = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showAddImagesDialog, setShowAddImagesDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [expandedProject, setExpandedProject] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newImageUrls, setNewImageUrls] = useState(['']);
  const [addImagesProjectId, setAddImagesProjectId] = useState(null);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    location: '',
    area: '',
    year: '',
    client_name: '',
    category: 'terase',
    materials: '',
    featured: false,
    images: []
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      toast.error('Eroare la încărcarea proiectelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSetCover = async (projectId, imageUrl) => {
    try {
      await axios.post(`${API}/admin/portfolio/${projectId}/set-cover`, 
        { cover_image: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Imagine de expunere setată');
      fetchProjects();
    } catch (error) {
      console.error('Error setting cover:', error);
      toast.error('Eroare la setarea imaginii de expunere');
    }
  };

  const handleMoveImages = async () => {
    if (!selectedProject || !moveTargetId || selectedImages.length === 0) {
      toast.error('Selectează proiectul destinație și imaginile');
      return;
    }

    try {
      await axios.post(`${API}/admin/portfolio/move-images`, {
        source_project_id: selectedProject.id,
        target_project_id: moveTargetId,
        image_urls: selectedImages
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`${selectedImages.length} imagini mutate cu succes`);
      setSelectedImages([]);
      setShowMoveDialog(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      console.error('Error moving images:', error);
      toast.error('Eroare la mutarea imaginilor');
    }
  };

  const handleAddImages = async () => {
    if (!addImagesProjectId) return;
    
    const validUrls = newImageUrls.filter(url => url.trim() !== '');
    if (validUrls.length === 0) {
      toast.error('Adaugă cel puțin o imagine');
      return;
    }

    try {
      const project = projects.find(p => p.id === addImagesProjectId);
      const updatedImages = [...(project.images || []), ...validUrls];
      
      await axios.put(`${API}/admin/portfolio/${addImagesProjectId}`, 
        { images: updatedImages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${validUrls.length} imagini adăugate`);
      setShowAddImagesDialog(false);
      setNewImageUrls(['']);
      setAddImagesProjectId(null);
      fetchProjects();
    } catch (error) {
      console.error('Error adding images:', error);
      toast.error('Eroare la adăugarea imaginilor');
    }
  };

  const handleDeleteImage = async (projectId, imageUrl) => {
    if (!window.confirm('Sigur doriți să ștergeți această imagine?')) return;
    
    try {
      const project = projects.find(p => p.id === projectId);
      const updatedImages = project.images.filter(img => img !== imageUrl);
      
      // Also clear cover_image if it was the deleted image
      const updateData = { images: updatedImages };
      if (project.cover_image === imageUrl) {
        updateData.cover_image = updatedImages[0] || null;
      }
      
      await axios.put(`${API}/admin/portfolio/${projectId}`, 
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Imagine ștearsă');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Eroare la ștergerea imaginii');
    }
  };

  const openAddImagesDialog = (projectId) => {
    setAddImagesProjectId(projectId);
    setNewImageUrls(['']);
    setShowAddImagesDialog(true);
  };

  const addNewImageField = () => {
    setNewImageUrls([...newImageUrls, '']);
  };

  const updateNewImageUrl = (index, value) => {
    const updated = [...newImageUrls];
    updated[index] = value;
    setNewImageUrls(updated);
  };

  const removeNewImageField = (index) => {
    setNewImageUrls(newImageUrls.filter((_, i) => i !== index));
  };

  const handleUpdateProject = async () => {
    if (!editingProject) return;

    try {
      const updateData = {
        title: editingProject.title,
        description: editingProject.description,
        location: editingProject.location,
        area: editingProject.area,
        year: editingProject.year,
        client_name: editingProject.client_name,
        category: editingProject.category,
        materials: editingProject.materials || [],
        featured: editingProject.featured
      };

      await axios.put(`${API}/admin/portfolio/${editingProject.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Proiect actualizat');
      setShowEditDialog(false);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Eroare la actualizarea proiectului');
    }
  };

  const handleAddProject = async () => {
    try {
      const projectData = {
        ...newProject,
        materials: newProject.materials.split(',').map(m => m.trim()).filter(m => m)
      };

      await axios.post(`${API}/admin/portfolio`, projectData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Proiect adăugat');
      setShowAddDialog(false);
      setNewProject({
        title: '',
        description: '',
        location: '',
        area: '',
        year: '',
        client_name: '',
        category: 'terase',
        materials: '',
        featured: false,
        images: []
      });
      fetchProjects();
    } catch (error) {
      console.error('Error adding project:', error);
      toast.error('Eroare la adăugarea proiectului');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest proiect?')) return;

    try {
      await axios.delete(`${API}/admin/portfolio/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proiect șters');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Eroare la ștergerea proiectului');
    }
  };

  const toggleImageSelection = (imageUrl) => {
    setSelectedImages(prev => 
      prev.includes(imageUrl) 
        ? prev.filter(img => img !== imageUrl)
        : [...prev, imageUrl]
    );
  };

  const openMoveDialog = (project) => {
    setSelectedProject(project);
    setSelectedImages([]);
    setMoveTargetId('');
    setShowMoveDialog(true);
  };

  if (loading) {
    return <div className="p-4 text-center">Se încarcă...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Gestiune Portofoliu</h2>
          <p className="text-slate-600">{projects.length} proiecte • Setează cover, mută imagini între proiecte</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Proiect
        </Button>
      </div>

      {/* Projects List */}
      <div className="space-y-4">
        {projects.map((project) => (
          <Card key={project.id} className="overflow-hidden" data-testid={`project-card-${project.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {/* Cover Image Preview */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                    <img 
                      src={project.cover_image || project.images?.[0] || '/placeholder.jpg'} 
                      alt={project.title}
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.src = 'https://via.placeholder.com/64'}
                    />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {project.title}
                      {project.featured && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                          FEATURED
                        </span>
                      )}
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      {project.location} • {project.area} • {project.year} • {project.images?.length || 0} imagini
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openAddImagesDialog(project.id)}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    data-testid={`add-images-${project.id}`}
                  >
                    <ImagePlus className="w-4 h-4 mr-1" />
                    Adaugă Poze
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openMoveDialog(project)}
                    data-testid={`move-images-${project.id}`}
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-1" />
                    Mută
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setEditingProject({...project, materials: project.materials || []});
                      setShowEditDialog(true);
                    }}
                    data-testid={`edit-project-${project.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteProject(project.id)}
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedProject(expandedProject === project.id ? null : project.id)}
                  >
                    {expandedProject === project.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            {/* Expanded Images Section */}
            {expandedProject === project.id && (
              <CardContent className="pt-2 border-t">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">
                    Imagini ({project.images?.length || 0}) - Hover: ⭐ setează cover, 🗑️ șterge
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAddImagesDialog(project.id)}
                    className="text-green-600"
                  >
                    <ImagePlus className="w-4 h-4 mr-1" />
                    Adaugă Poze
                  </Button>
                </div>
                <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                  {project.images?.map((img, idx) => (
                    <div 
                      key={idx} 
                      className={`relative group aspect-square rounded-lg overflow-hidden border-2 ${
                        project.cover_image === img ? 'border-orange-500' : 'border-transparent'
                      }`}
                    >
                      <img 
                        src={img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                        alt={`Imagine ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/100'}
                      />
                      {/* Overlay pentru acțiuni */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleSetCover(project.id, img)}
                          className={`p-1.5 rounded-full ${
                            project.cover_image === img 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-white/90 text-slate-700 hover:bg-orange-500 hover:text-white'
                          }`}
                          title="Setează ca imagine de expunere"
                        >
                          {project.cover_image === img ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteImage(project.id, img)}
                          className="p-1.5 rounded-full bg-white/90 text-red-600 hover:bg-red-500 hover:text-white"
                          title="Șterge imaginea"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {/* Cover badge */}
                      {project.cover_image === img && (
                        <div className="absolute top-1 left-1 px-1 py-0.5 bg-orange-500 text-white text-xs rounded">
                          Cover
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {(!project.images || project.images.length === 0) && (
                  <p className="text-sm text-slate-500 text-center py-4">Nu există imagini pentru acest proiect</p>
                )}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <Image className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600">Nu există proiecte în portofoliu</p>
          <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
            Adaugă Primul Proiect
          </Button>
        </div>
      )}

      {/* Move Images Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mută Imagini - {selectedProject?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Target project selector */}
            <div>
              <label className="block text-sm font-medium mb-2">Selectează proiectul destinație:</label>
              <Select value={moveTargetId} onValueChange={setMoveTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Alege proiectul..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.id !== selectedProject?.id).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title} ({p.images?.length || 0} imagini)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Images to select */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Selectează imaginile de mutat ({selectedImages.length} selectate):
              </label>
              <div className="grid grid-cols-6 md:grid-cols-8 gap-2 max-h-[400px] overflow-y-auto p-2 bg-slate-50 rounded-lg">
                {selectedProject?.images?.map((img, idx) => (
                  <div 
                    key={idx}
                    onClick={() => toggleImageSelection(img)}
                    className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 ${
                      selectedImages.includes(img) ? 'border-cyan-500 ring-2 ring-cyan-500/50' : 'border-transparent'
                    }`}
                  >
                    <img 
                      src={img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                      alt={`Imagine ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {selectedImages.includes(img) && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>
              Anulează
            </Button>
            <Button 
              onClick={handleMoveImages}
              disabled={!moveTargetId || selectedImages.length === 0}
            >
              Mută {selectedImages.length} Imagini
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editează Proiect</DialogTitle>
          </DialogHeader>
          
          {editingProject && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titlu</label>
                <Input 
                  value={editingProject.title}
                  onChange={(e) => setEditingProject({...editingProject, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descriere</label>
                <Textarea 
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({...editingProject, description: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Locație</label>
                  <Input 
                    value={editingProject.location || ''}
                    onChange={(e) => setEditingProject({...editingProject, location: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Suprafață</label>
                  <Input 
                    value={editingProject.area || ''}
                    onChange={(e) => setEditingProject({...editingProject, area: e.target.value})}
                    placeholder="ex: 7000 mp"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">An</label>
                  <Input 
                    value={editingProject.year || ''}
                    onChange={(e) => setEditingProject({...editingProject, year: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Categorie</label>
                  <Select 
                    value={editingProject.category || 'terase'} 
                    onValueChange={(val) => setEditingProject({...editingProject, category: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client</label>
                <Input 
                  value={editingProject.client_name || ''}
                  onChange={(e) => setEditingProject({...editingProject, client_name: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={editingProject.featured || false}
                  onCheckedChange={(checked) => setEditingProject({...editingProject, featured: checked})}
                />
                <label className="text-sm">Proiect Featured (afișat prominent)</label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Anulează
            </Button>
            <Button onClick={handleUpdateProject}>
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Project Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adaugă Proiect Nou</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titlu *</label>
              <Input 
                value={newProject.title}
                onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                placeholder="ex: Facultatea de Chimie - UTI"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Descriere</label>
              <Textarea 
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                rows={3}
                placeholder="Descriere detaliată a proiectului..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Locație</label>
                <Input 
                  value={newProject.location}
                  onChange={(e) => setNewProject({...newProject, location: e.target.value})}
                  placeholder="ex: Iași"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Suprafață</label>
                <Input 
                  value={newProject.area}
                  onChange={(e) => setNewProject({...newProject, area: e.target.value})}
                  placeholder="ex: 7000 mp"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">An</label>
                <Input 
                  value={newProject.year}
                  onChange={(e) => setNewProject({...newProject, year: e.target.value})}
                  placeholder="ex: 2023"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Categorie</label>
                <Select 
                  value={newProject.category} 
                  onValueChange={(val) => setNewProject({...newProject, category: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Client</label>
              <Input 
                value={newProject.client_name}
                onChange={(e) => setNewProject({...newProject, client_name: e.target.value})}
                placeholder="ex: Universitatea Tehnică Gheorghe Asachi"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Materiale (separate prin virgulă)</label>
              <Input 
                value={newProject.materials}
                onChange={(e) => setNewProject({...newProject, materials: e.target.value})}
                placeholder="ex: Membrană bituminoasă, Izolație PIR, Barieră vapori"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={newProject.featured}
                onCheckedChange={(checked) => setNewProject({...newProject, featured: checked})}
              />
              <label className="text-sm">Proiect Featured</label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Anulează
            </Button>
            <Button onClick={handleAddProject} disabled={!newProject.title}>
              Adaugă Proiect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Images Dialog */}
      <Dialog open={showAddImagesDialog} onOpenChange={setShowAddImagesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adaugă Poze Noi</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Adaugă URL-urile imaginilor. Poți folosi:
              <br />• Link-uri externe (https://...)
              <br />• Imagini locale (/images/portfolio/imageX.jpg)
            </p>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {newImageUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={url}
                    onChange={(e) => updateNewImageUrl(index, e.target.value)}
                    placeholder="/images/portfolio/image37.jpg sau https://..."
                    className="flex-1"
                  />
                  {newImageUrls.length > 1 && (
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeNewImageField(index)}
                      className="text-red-500 px-2"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addNewImageField}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-1" />
              Adaugă Alt URL
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddImagesDialog(false)}>
              Anulează
            </Button>
            <Button 
              onClick={handleAddImages}
              disabled={newImageUrls.every(url => !url.trim())}
            >
              <ImagePlus className="w-4 h-4 mr-1" />
              Adaugă {newImageUrls.filter(u => u.trim()).length} Imagini
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioManager;
